import React, { useEffect, useMemo, useRef, useState } from "react";
import { Mafs, Coordinates, Line, Theme, Text } from "mafs";

/**
 * Sampling Simulator Widget (feedback visualisation)
 *
 * Flagship upgrades:
 * - Side-by-side comparison mode (default): compare n=5 vs n=30 on the SAME population.
 * - Fast "Run 100" button to make the "wow" immediate.
 * - Clear callouts: population μ/σ, expected SE, observed spread.
 * - Misconception hook: short, always-visible explanation line.
 * - Demo reliability: guaranteed non-blank defaults + reset always works.
 */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const safe = (n, fallback) => (Number.isFinite(n) ? n : fallback);

// Mafs versions vary slightly; prefer Theme colors when present, otherwise fall back to safe CSS colors.
const COLOR_A = Theme?.indigo ?? "#4f46e5";
const COLOR_B = Theme?.teal ?? "#0f766e";
const COLOR_MU = Theme?.red ?? "#dc2626";
const COLOR_POP = Theme?.gray ?? "rgba(0,0,0,0.25)";


// --- Deterministic RNG (so "replay same seed" is possible if provided) ---
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Box–Muller
const normal = (rand, mean, sd) => {
  const u1 = Math.max(1e-12, rand());
  const u2 = Math.max(1e-12, rand());
  const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
  return mean + z0 * sd;
};

const sampleFromDistribution = (rand, dist) => {
  const type = (dist?.type || "NORMAL").toString().trim().toUpperCase();
  const mean = safe(dist?.mean, 50);
  const sd = Math.max(1e-6, Math.abs(safe(dist?.stdDev, 10)));
  const min = safe(dist?.min, 0);
  const max = safe(dist?.max, 100);

  switch (type) {
    case "UNIFORM": {
      const a = Math.min(min, max);
      const b = Math.max(min, max);
      return a + rand() * (b - a);
    }
    case "SKEWED": {
      // quick right-skew via gamma-ish shape
      const u = Math.max(1e-12, rand());
      const skew = Math.exp(-Math.log(u) * 1.2);
      return mean + (skew - 1) * sd;
    }
    case "BIMODAL": {
      const mix = rand() < 0.5;
      const m = mix ? mean - 1.5 * sd : mean + 1.5 * sd;
      return normal(rand, m, sd * 0.6);
    }
    case "NORMAL":
    default:
      return normal(rand, mean, sd);
  }
};

const meanOf = (arr) => {
  if (!arr || arr.length === 0) return NaN;
  let s = 0;
  for (const v of arr) s += v;
  return s / arr.length;
};

const sdOf = (arr) => {
  if (!arr || arr.length < 2) return NaN;
  const m = meanOf(arr);
  let s2 = 0;
  for (const v of arr) s2 += (v - m) * (v - m);
  return Math.sqrt(s2 / (arr.length - 1));
};

const buildHistogram = (values, bins, minX, maxX) => {
  if (!values || values.length === 0) return { bins: [], maxCount: 0, minX: 0, maxX: 1 };
  const vmin = Number.isFinite(minX) ? minX : Math.min(...values);
  const vmax = Number.isFinite(maxX) ? maxX : Math.max(...values);
  const lo = vmin === vmax ? vmin - 1 : vmin;
  const hi = vmin === vmax ? vmax + 1 : vmax;

  const w = (hi - lo) / bins;
  const counts = new Array(bins).fill(0);
  for (const v of values) {
    const idx = clamp(Math.floor((v - lo) / w), 0, bins - 1);
    counts[idx]++;
  }
  const maxCount = Math.max(1, ...counts);
  const out = counts.map((c, i) => ({ x0: lo + i * w, x1: lo + (i + 1) * w, c }));
  return { bins: out, maxCount, minX: lo, maxX: hi };
};

export const SamplingSimulator = ({ config }) => {
  const cfg = config || {};

  // --- Config with robust defaults (never blank) ---
  const population = cfg.population || { type: "NORMAL", mean: 50, stdDev: 10, min: 0, max: 100 };
  const showPopulation = cfg.showPopulation !== false;
  const animateSampling = cfg.animateSampling === true;

  const comparisonModeDefault = cfg.comparisonMode !== false; // default ON for flagship
  const nSmallDefault = clamp(Math.floor(safe(cfg.compareSampleSizeSmall, 5)), 1, 500);
  const nLargeDefault = clamp(Math.floor(safe(cfg.compareSampleSizeLarge, 30)), 2, 500);

  const sampleSizeDefault = clamp(Math.floor(safe(cfg.sampleSize, 30)), 1, 500);
  const maxSamples = clamp(Math.floor(safe(cfg.numberOfSamples, 200)), 10, 2000);

  const [comparisonMode, setComparisonMode] = useState(comparisonModeDefault);
  const [nSmall, setNSmall] = useState(nSmallDefault);
  const [nLarge, setNLarge] = useState(nLargeDefault);
  const [singleN, setSingleN] = useState(sampleSizeDefault);

  // Seed handling (optional): if provided, allow replaying the same run.
  const seed = Number.isFinite(cfg.seed) ? Math.floor(cfg.seed) : null;
  const rngRef = useRef(mulberry32(seed ?? Math.floor(Math.random() * 1e9)));
  const reseed = (useSame) => {
    rngRef.current = mulberry32(useSame && seed != null ? seed : Math.floor(Math.random() * 1e9));
  };

  // --- State (comparison uses A/B; single uses A only) ---
  const [meansA, setMeansA] = useState([]);
  const [meansB, setMeansB] = useState([]);
  const meansARef = useRef(0);
  const meansBRef = useRef(0);
  const [lastSampleA, setLastSampleA] = useState([]);
  const [lastSampleB, setLastSampleB] = useState([]);
  const [running, setRunning] = useState(false);
  const [useSameSeed, setUseSameSeed] = useState(seed != null);

  useEffect(() => { meansARef.current = meansA.length; }, [meansA.length]);
  useEffect(() => { meansBRef.current = meansB.length; }, [meansB.length]);

  const mu = safe(population.mean, 50);
  const sigma = Math.max(1e-6, Math.abs(safe(population.stdDev, 10)));

  const expectedSE = (n) => sigma / Math.sqrt(Math.max(1, n));

  const takeOneSampleMean = (n) => {
    const rand = rngRef.current;
    const sample = new Array(n);
    for (let i = 0; i < n; i++) sample[i] = sampleFromDistribution(rand, population);
    const m = meanOf(sample);
    return { sample, mean: m };
  };

  const hardReset = () => {
    setRunning(false);
    reseed(useSameSeed);
    setMeansA([]);
    setMeansB([]);
    setLastSampleA([]);
    setLastSampleB([]);
  };

  const runBatch = (count) => {
    const batch = Math.max(1, Math.floor(count));
    const nA = comparisonMode ? nSmall : singleN;
    const nB = comparisonMode ? nLarge : null;

    setMeansA((prev) => {
      const out = prev.slice();
      const room = maxSamples - out.length;
      const k = Math.min(batch, room);
      for (let i = 0; i < k; i++) out.push(takeOneSampleMean(nA).mean);
      return out;
    });

    if (comparisonMode && nB != null) {
      setMeansB((prev) => {
        const out = prev.slice();
        const room = maxSamples - out.length;
        const k = Math.min(batch, room);
        for (let i = 0; i < k; i++) out.push(takeOneSampleMean(nB).mean);
        return out;
      });
    }

    // Update "current sample" preview using the LAST draw (keeps interaction immediate)
    const oneA = takeOneSampleMean(nA);
    setLastSampleA(oneA.sample);
    if (comparisonMode && nB != null) {
      const oneB = takeOneSampleMean(nB);
      setLastSampleB(oneB.sample);
    } else {
      setLastSampleB([]);
    }
  };

  // --- Animation loop (optional; batch still the flagship interaction) ---
  useEffect(() => {
    if (!running) return undefined;
    if (!animateSampling) return undefined;

    const t = window.setInterval(() => {
      runBatch(1);
      // Auto-stop (use refs to avoid stale closure lengths)
      const doneA = meansARef.current >= maxSamples;
      const doneB = !comparisonMode || meansBRef.current >= maxSamples;
      if (doneA && doneB) setRunning(false);
    }, 90);

    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, animateSampling, comparisonMode, maxSamples]);

  // --- Guaranteed interesting default state ---
  useEffect(() => {
    // Whenever the mode or key parameters change, restart with a small non-zero sample.
    hardReset();
    // Make it immediately non-blank.
    runBatch(Math.min(30, maxSamples));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonMode, nSmall, nLarge, singleN]);

  // --- Derived stats + view boxes ---
  const statsA = useMemo(() => {
    const m = meanOf(meansA);
    const s = sdOf(meansA);
    return { mean: m, sd: s, n: comparisonMode ? nSmall : singleN };
  }, [meansA, comparisonMode, nSmall, singleN]);

  const statsB = useMemo(() => {
    if (!comparisonMode) return null;
    const m = meanOf(meansB);
    const s = sdOf(meansB);
    return { mean: m, sd: s, n: nLarge };
  }, [meansB, comparisonMode, nLarge]);

  const combinedRange = useMemo(() => {
    const all = comparisonMode ? meansA.concat(meansB) : meansA;
    if (all.length === 0) return { min: mu - 4 * sigma, max: mu + 4 * sigma };
    const min = Math.min(...all);
    const max = Math.max(...all);
    const pad = Math.max(1e-6, 0.1 * (max - min || 1));
    return { min: min - pad, max: max + pad };
  }, [comparisonMode, meansA, meansB, mu, sigma]);

  const histogramA = useMemo(() => {
    return buildHistogram(meansA, 12, combinedRange.min, combinedRange.max);
  }, [meansA, combinedRange]);

  const histogramB = useMemo(() => {
    if (!comparisonMode) return null;
    return buildHistogram(meansB, 12, combinedRange.min, combinedRange.max);
  }, [meansB, combinedRange, comparisonMode]);

  const viewBox = useMemo(() => {
    // Ensure maxCount is at least 1 to avoid a collapsed viewbox
    const maxCount = Math.max(histogramA.maxCount || 1, histogramB?.maxCount || 1);
    const yMax = maxCount * 1.15;
    return { x: [combinedRange.min, combinedRange.max], y: [-1, yMax] };
  }, [combinedRange, histogramA.maxCount, histogramB?.maxCount]);

  // --- Population preview points (cheap + smooth) ---
  const populationPoints = useMemo(() => {
    if (!showPopulation) return [];
    const rand = mulberry32(12345); // stable preview
    const pts = [];
    for (let i = 0; i < 120; i++) {
      const x = sampleFromDistribution(rand, population);
      const jitter = (rand() - 0.5) * 0.6;
      pts.push([x, jitter]);
    }
    return pts;
  }, [showPopulation, population]);

  const renderPopulationPoints = (points) => {
    if (!points || points.length === 0) return null;

    // Use tiny line segments as "dots" so we don't depend on Plot.OfPoints / Plot.Point APIs.
    // This avoids runtime crashes when Mafs versions differ.
    const half = 0.06;
    return points.map((p, i) => {
      const x = p[0];
      const y = p[1];
      return (
          <Line.Segment
              key={`pop-${i}`}
              point1={[x, y - half]}
              point2={[x, y + half]}
              color={COLOR_POP}
              weight={2}
          />
      );
    });
  };

  const renderHistogram = (hist, color) => {
    if (!hist || !hist.bins || hist.bins.length === 0) return null;

    return (
        <React.Fragment key={`hist-animate-${meansA.length}`}>
          {hist.bins.flatMap((b, i) => {
            const y = b.c;
            return [
              <Line.Segment
                  key={`left-${i}`}
                  point1={[b.x0, 0]}
                  point2={[b.x0, y]}
                  color={color}
                  weight={3}
                  style={{ animation: 'ch-bar-impact 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards', transformOrigin: 'bottom' }}
              />,
              <Line.Segment
                  key={`right-${i}`}
                  point1={[b.x1, 0]}
                  point2={[b.x1, y]}
                  color={color}
                  weight={3}
                  style={{ animation: 'ch-bar-impact 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards', transformOrigin: 'bottom' }}
              />,
              <Line.Segment
                  key={`top-${i}`}
                  point1={[b.x0, y]}
                  point2={[b.x1, y]}
                  color={color}
                  weight={3}
                  style={{ animation: 'ch-bar-impact 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards', transformOrigin: 'bottom' }}
              />
            ];
          })}
        </React.Fragment>
    );
  };

  const convergenceBadge = (obsSD, expSE) => {
    if (!Number.isFinite(obsSD) || !Number.isFinite(expSE) || expSE <= 0) return "—";
    const ratio = obsSD / expSE;
    if (ratio >= 0.8 && ratio <= 1.25) return "converging";
    if (ratio < 0.8) return "tighter than expected";
    return "still noisy";
  };

  const fmt = (n) => {
    if (!Number.isFinite(n)) return "—";
    if (Math.abs(n) >= 1000) return n.toFixed(0);
    if (Math.abs(n) >= 10) return n.toFixed(1);
    return n.toFixed(2);
  };

  const canRunMore = meansA.length < maxSamples && (!comparisonMode || meansB.length < maxSamples);

  return (
      <div className="ch-sampling-widget">
        <div className="ch-sampling-header">
          <div>
            <h3>Sampling Simulator</h3>
            <div className="ch-sampling-subtitle">
              Sample means approach μ as trials increase; spread shrinks as n grows.
            </div>
          </div>

          <div className="ch-sampling-actions">
            <button className="ch-btn-outline" onClick={hardReset} type="button">Reset</button>
          </div>
        </div>

        {/* Mode controls (one obvious interaction) */}
        <div className="ch-emph-block ch-sampling-controls">
          <div className="ch-sampling-control-row">
            <button
                type="button"
                className={comparisonMode ? "ch-btn" : "ch-btn-outline"}
                onClick={() => setComparisonMode(true)}
                title="Compare two sample sizes"
            >
              Compare n={nSmall} vs n={nLarge}
            </button>
            <button
                type="button"
                className={!comparisonMode ? "ch-btn" : "ch-btn-outline"}
                onClick={() => setComparisonMode(false)}
                title="Single sample size view"
            >
              Single n={singleN}
            </button>

            <span className="ch-sampling-spacer" />

            <button
                type="button"
                className="ch-btn"
                disabled={!canRunMore}
                onClick={() => runBatch(100)}
                title="Run 100 samples quickly"
            >
              Run 100
            </button>
            <button
                type="button"
                className="ch-btn-outline"
                disabled={!canRunMore}
                onClick={() => runBatch(10)}
                title="Run 10 samples"
            >
              Run 10
            </button>
            <button
                type="button"
                className="ch-btn-outline"
                disabled={!animateSampling || !canRunMore}
                onClick={() => setRunning((r) => !r)}
                title="Animated mode (optional)"
            >
              {running ? "Pause" : "Animate"}
            </button>

            {seed != null && (
                <label className="ch-sampling-seed">
                  <input
                      type="checkbox"
                      checked={useSameSeed}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setUseSameSeed(v);
                        reseed(v);
                      }}
                  />
                  Same seed
                </label>
            )}
          </div>

          {/* Small parameter n editors for tutor/demo use (kept lightweight) */}
          <div className="ch-sampling-control-row ch-sampling-smallrow">
            {comparisonMode ? (
                <>
                  <span className="ch-sampling-control-label">n small</span>
                  <input
                      className="ch-sampling-num"
                      type="number"
                      min={1}
                      max={500}
                      value={nSmall}
                      onChange={(e) => setNSmall(clamp(parseInt(e.target.value || "5", 10), 1, 500))}
                  />
                  <span className="ch-sampling-control-label">n large</span>
                  <input
                      className="ch-sampling-num"
                      type="number"
                      min={2}
                      max={500}
                      value={nLarge}
                      onChange={(e) => setNLarge(clamp(parseInt(e.target.value || "30", 10), 2, 500))}
                  />
                </>
            ) : (
                <>
                  <span className="ch-sampling-control-label">n</span>
                  <input
                      className="ch-sampling-num"
                      type="number"
                      min={1}
                      max={500}
                      value={singleN}
                      onChange={(e) => setSingleN(clamp(parseInt(e.target.value || "30", 10), 1, 500))}
                  />
                </>
            )}
            <span className="ch-sampling-muted">Samples: {meansA.length}{comparisonMode ? ` / ${meansB.length}` : ""} (max {maxSamples})</span>
          </div>
        </div>

        {/* Charts */}
        <div className={comparisonMode ? "ch-sampling-charts is-compare" : "ch-sampling-charts"}>
          <div className="ch-sampling-chart">
            <div className="ch-sampling-chart-title">
              Distribution of sample means (n={statsA.n})
            </div>
            <Mafs viewBox={viewBox} pan={false} zoom={false}>
              <Coordinates.Cartesian />
              {showPopulation && renderPopulationPoints(populationPoints)}
              {renderHistogram(histogramA, COLOR_A)}
              {/* Population mean μ */}
              <Line.Segment point1={[mu, viewBox.y[0]]} point2={[mu, viewBox.y[1]]} color={COLOR_MU} weight={2} />
              <Text x={mu} y={viewBox.y[1] - 0.6} color={COLOR_MU} size={14}>μ</Text>
              {/* Observed mean */}
              {Number.isFinite(statsA.mean) && (
                  <>
                    <Line.Segment point1={[statsA.mean, viewBox.y[0]]} point2={[statsA.mean, viewBox.y[1]]} color={COLOR_A} weight={2} />
                    <Text x={statsA.mean} y={viewBox.y[1] - 1.2} color={COLOR_A} size={14}>x̄</Text>
                  </>
              )}
            </Mafs>
          </div>

          {comparisonMode && (
              <div className="ch-sampling-chart">
                <div className="ch-sampling-chart-title">
                  Distribution of sample means (n={statsB?.n})
                </div>
                <Mafs viewBox={viewBox} pan={false} zoom={false}>
                  <Coordinates.Cartesian />
                  {showPopulation && renderPopulationPoints(populationPoints)}
                  {renderHistogram(histogramB, COLOR_B)}
                  <Line.Segment point1={[mu, viewBox.y[0]]} point2={[mu, viewBox.y[1]]} color={COLOR_MU} weight={2} />
                  <Text x={mu} y={viewBox.y[1] - 0.6} color={COLOR_MU} size={14}>μ</Text>
                  {statsB && Number.isFinite(statsB.mean) && (
                      <>
                        <Line.Segment point1={[statsB.mean, viewBox.y[0]]} point2={[statsB.mean, viewBox.y[1]]} color={COLOR_B} weight={2} />
                        <Text x={statsB.mean} y={viewBox.y[1] - 1.2} color={COLOR_B} size={14}>x̄</Text>
                      </>
                  )}
                </Mafs>
              </div>
          )}
        </div>

        {/* Key callouts (the "wow") */}
        <div className="ch-sampling-metrics">
          <div className="ch-sampling-metric">
            <div className="ch-sampling-metric-title">Population</div>
            <div className="ch-sampling-metric-value">μ={fmt(mu)}, σ={fmt(sigma)}</div>
          </div>

          <div className="ch-sampling-metric">
            <div className="ch-sampling-metric-title">n={statsA.n}</div>
            <div className="ch-sampling-metric-value">
              expected SE={fmt(expectedSE(statsA.n))} · observed spread={fmt(statsA.sd)}
              <span className="ch-sampling-pill">{convergenceBadge(statsA.sd, expectedSE(statsA.n))}</span>
            </div>
          </div>

          {comparisonMode && statsB && (
              <div className="ch-sampling-metric">
                <div className="ch-sampling-metric-title">n={statsB.n}</div>
                <div className="ch-sampling-metric-value">
                  expected SE={fmt(expectedSE(statsB.n))} · observed spread={fmt(statsB.sd)}
                  <span className="ch-sampling-pill">{convergenceBadge(statsB.sd, expectedSE(statsB.n))}</span>
                </div>
              </div>
          )}

          <div className="ch-sampling-metric">
            <div className="ch-sampling-metric-title">Tightness</div>
            <div className="ch-sampling-metric-value">
              Spread of x̄ decreases as n increases (roughly ∝ 1/√n).
            </div>
          </div>
        </div>

        {/* Misconception hook */}
        <div className="ch-sampling-caption">
          <span className="ch-sampling-strong">Note:</span> A single sample mean can be far from μ.
          With more samples, the distribution of sample means becomes clearer, and larger n makes it tighter.
        </div>

        {/* Optional: show the last sampled points to keep "randomness" tangible */}
        <div className="ch-sampling-last">
          <div className="ch-sampling-last-title">Last sample (so you can see randomness)</div>
          <div className="ch-sampling-last-row">
            <div className="ch-sampling-last-block">
              <div className="ch-sampling-last-label">n={comparisonMode ? nSmall : singleN}</div>
              <div className="ch-sampling-last-values">{lastSampleA.slice(0, 18).map((v, i) => <span key={`a-${i}`}>{fmt(v)}</span>)}</div>
            </div>
            {comparisonMode && (
                <div className="ch-sampling-last-block">
                  <div className="ch-sampling-last-label">n={nLarge}</div>
                  <div className="ch-sampling-last-values">{lastSampleB.slice(0, 18).map((v, i) => <span key={`b-${i}`}>{fmt(v)}</span>)}</div>
                </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default SamplingSimulator;
